/**
 * SerpFeaturesCell — compact chips showing which SERP features appear for a
 * keyword's result. Owned features (client's domain cited inside the
 * feature) render solid teal; unowned features render as soft grey outlines.
 *
 * Each chip has a rich hover tooltip explaining what the feature is and
 * why it matters — so the team doesn't need to memorise acronyms.
 *
 * Data comes from `results[i].serp_features` + `results[i].owned_features`
 * on each rank snapshot (scheduler.py extracts these on every SERP call).
 */
import React from "react";
import {
  Sparkles, HelpCircle, Award, Store, Video, Image as ImageIcon,
  Newspaper, ShoppingBag, Search, CheckCircle2,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../../ui/tooltip";

// Ordered by SEO priority — most valuable features render leftmost so the
// eye picks them up first when scanning a busy table. `desc` powers the
// tooltip body and explains what the feature is + why it matters.
const FEATURE_META = {
  ai_overview: {
    label: "AI Overview",
    short: "AIO",
    icon: Sparkles,
    priority: 1,
    desc: "Google's generative AI answer at the very top of the results page. Being cited here is the single strongest visibility signal in 2026 — it often replaces the classic #1 organic spot.",
  },
  featured_snippet: {
    label: "Featured Snippet",
    short: "FS",
    icon: Award,
    priority: 2,
    desc: "The boxed answer pulled from a page and shown above position #1 (aka \"position zero\"). Typically drives 8-12% of clicks for the query and steals traffic from ranks 2-10.",
  },
  people_also_ask: {
    label: "People Also Ask",
    short: "PAA",
    icon: HelpCircle,
    priority: 3,
    desc: "The expandable question/answer block on the SERP. Getting cited surfaces the domain multiple times on the same page — great for brand-awareness on informational queries.",
  },
  knowledge_graph: {
    label: "Knowledge Graph",
    short: "KG",
    icon: Store,
    priority: 4,
    desc: "The right-hand entity panel (usually a business, place or person). Signals Google recognises the entity — commonly powered by GBP for local businesses.",
  },
  video: {
    label: "Video Pack",
    short: "VID",
    icon: Video,
    priority: 5,
    desc: "A row of YouTube / video thumbnails inside the SERP. Suggests the query has strong video intent — worth having a YouTube presence for this keyword.",
  },
  top_stories: {
    label: "Top Stories",
    short: "NEWS",
    icon: Newspaper,
    priority: 6,
    desc: "News carousel at the top of the SERP. Indicates the query has trending/news intent this week — usually reserved for publishers.",
  },
  images: {
    label: "Image Pack",
    short: "IMG",
    icon: ImageIcon,
    priority: 7,
    desc: "A row of image thumbnails on the SERP. Google is showing visual intent — optimised image alt text + filenames can win clicks here.",
  },
  shopping: {
    label: "Shopping",
    short: "SHOP",
    icon: ShoppingBag,
    priority: 8,
    desc: "Product listings / Google Shopping carousel. Indicates commercial / transactional intent — often paid but organic product schema can rank too.",
  },
  related_searches: {
    label: "Related Searches",
    short: "REL",
    icon: Search,
    priority: 9,
    desc: "The \"People also search for\" cluster at the bottom of the SERP. Useful for expanding your keyword list — the queries listed reveal how users refine this search.",
  },
};

function FeatureChip({ feature, isOwned }) {
  const meta = FEATURE_META[feature] || {
    label: feature,
    short: feature.slice(0, 3).toUpperCase(),
    desc: "SERP feature detected on this keyword's result.",
    icon: Search,
  };
  const Icon = meta.icon;
  const isAIO = feature === "ai_overview";
  const cls = isOwned
    ? isAIO
      ? "bg-[#f59e0b] text-white border-[#d97706]"
      : "bg-[#38b2ac] text-white border-[#2c7a7b]"
    : isAIO
    ? "bg-[#fef3c7] text-[#92400e] border-[#fde68a]"
    : "bg-white text-[#718096] border-[#cbd5e0]";
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <span
          data-testid={`serp-feature-chip-${feature}`}
          className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-[1px] rounded border cursor-help ${cls}`}
        >
          {meta.short}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        className="max-w-[280px] bg-[#1a202c] text-white border border-[#2d3748] rounded-lg px-3 py-2.5 shadow-lg"
        data-testid={`serp-feature-tooltip-${feature}`}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <Icon className="h-3.5 w-3.5 text-[#4fd1c5]" strokeWidth={2.2} />
          <div className="text-[12px] font-bold text-white leading-tight">
            {meta.label}
            <span className="ml-1.5 text-[9px] font-normal text-[#a0aec0] uppercase tracking-widest">
              {meta.short}
            </span>
          </div>
        </div>
        <div className="text-[11px] leading-snug text-[#cbd5e0] normal-case tracking-normal font-normal">
          {meta.desc}
        </div>
        <div
          className={`mt-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest ${
            isOwned ? "text-[#4fd1c5]" : "text-[#718096]"
          }`}
        >
          {isOwned ? (
            <>
              <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={2.5} />
              You're cited here
            </>
          ) : (
            <>Appeared on the SERP — not yours</>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export default function SerpFeaturesCell({ present, owned }) {
  const list = Array.isArray(present) ? present : [];
  const ownedSet = new Set(Array.isArray(owned) ? owned : []);
  if (list.length === 0) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={150}>
          <TooltipTrigger asChild>
            <span
              className="text-[#cbd5e0] text-xs cursor-help"
              data-testid="serp-feature-empty"
            >
              ·
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-[240px] bg-[#1a202c] text-white border border-[#2d3748] rounded-lg px-3 py-2 shadow-lg"
          >
            <div className="text-[11px] leading-snug text-[#cbd5e0] normal-case tracking-normal">
              No tracked SERP features on this result — or this snapshot pre-dates the feature capture (Feb 2026+).
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  const ordered = [...list].sort(
    (a, b) => (FEATURE_META[a]?.priority ?? 99) - (FEATURE_META[b]?.priority ?? 99),
  );
  return (
    <TooltipProvider>
      <div className="flex items-center gap-1 flex-wrap justify-end">
        {ordered.map((f) => (
          <FeatureChip key={f} feature={f} isOwned={ownedSet.has(f)} />
        ))}
      </div>
    </TooltipProvider>
  );
}
